import {Component, Event, EventEmitter, h, Method, Prop, State} from '@stencil/core';
import {
    getCountryList,
    getLanguageList,
    IApiResponse,
} from "./util";
import {addApiResponseChangeListener, getApiResponse} from "./api";
import {generateUUID} from "../../utils/utils";


@Component({
    tag: 'dc-language-chooser-header',
    styleUrl: '../../assets/styles/style.scss',
    shadow: true,
})
export class DcLanguageChooserHeader {
    @Prop() apiUrl: string;
    @Prop() closeButtonAriaLabel: string;
    @Prop() countrySelectLabel: string;
    @Prop() languageSelectLabel: string;
    @Prop() heading: string;
    @Prop() mainButtonAriaLabel: string;

    @State() apiResponse: IApiResponse | null = null;

    @Event({ eventName: 'countrySelect' }) countrySelectEvent: EventEmitter<{ label: string, value: string }>;


    protected contentWrapperEle: HTMLDivElement;
    protected _isOpen = false;
    private countryUUID = generateUUID();
    private languageUUID = generateUUID();

    @Method()
    public async close() {
        if (!this.contentWrapperEle) {
            return;
        }

        this.contentWrapperEle.style.maxHeight = '0';
        this.contentWrapperEle.style.padding = '0';
        document.querySelector('body').classList.remove('body-no-scroll');
        this._isOpen = false;
    }

    async componentWillLoad() {
        const url = new URL(this.apiUrl);
        this.apiResponse = getApiResponse(url);
        addApiResponseChangeListener(url, newResponse => this.apiResponse = newResponse);
        document.querySelector('div.inline-language-select')?.addEventListener('click', () => this.toggle());
        document.querySelectorAll('button.btn.btn-link.toggle-flyout').forEach(
            ele => ele.addEventListener('click', () => this.close()));
    }

    @Method()
    public async isOpen() {
        return this._isOpen;
    }

    @Method()
    public async open() {
        if (!this.contentWrapperEle) {
            return;
        }

        this.contentWrapperEle.style.maxHeight = `500px`; // 500 is bigger than it should ever get
        this.contentWrapperEle.style.padding = '';
        document.querySelector('body').classList.add('body-no-scroll');
        this._isOpen = true;
    }

    private onSelectChange(event: UIEvent): void {
        const label = (event.target as HTMLSelectElement).selectedOptions[0].label;
        const url = (event.target as HTMLSelectElement).value;

        this.countrySelectEvent.emit({
            label,
            value: url,
        });

        if (url) {
            setTimeout(() => window.location.assign(url));
        }
    }

    @Method()
    public async toggle() {
        if (this._isOpen) {
            return this.close();
        } else {
            return this.open();
        }
    }

    render() {
        // only render if an APIResponse exists
        if (!this.apiResponse) {
            return;
        }

        const countries = getCountryList(this.apiResponse);

        const languages = getLanguageList(this.apiResponse);

        return <div class="module-language-location-select multi-language">
            <div
                class="language-select-wrapper flyout"
                ref={ele => this.contentWrapperEle = ele}
                style={{
                    display: 'block',
                    maxHeight: '0',
                    overflow: 'hidden',
                    padding: '0',
                    transition: 'max-height .5s ease-in-out, padding-top .5s ease-in-out, padding-bottom .5s ease-in-out',
                }}
            >
                <div class="close-button-wrapper">
                    <div class="module-button ">
                        <button
                            type="button"
                            class="btn btn-link btn-lg close-flyout"
                            aria-label={this.closeButtonAriaLabel}
                            onClick={() => this.close()}
                        ></button>
                        <div class="button-animation"></div>
                        <div class="button-content">
                            <i class="icon icon-dc_close"></i>
                        </div>
                    </div>
                </div>
                <p class='h2'>{this.heading}</p>
                <div class='selection-wrapper'>
                    <div class='module-dropdown location'>
                        <div class='input-animation-wrapper'>
                            <label htmlFor={this.countryUUID}>{this.countrySelectLabel}</label>
                            <select
                                id={this.countryUUID}
                                class='custom-select'
                                onChange={event => this.onSelectChange(event as UIEvent)}
                            >
                                {
                                    countries.map(country => <option
                                        selected={country.selected}
                                        value={country.value}
                                    >
                                        {country.label}
                                    </option>)
                                }
                            </select>
                            <div class='input-animation'></div>
                        </div>
                        <span class='error-message'></span>
                    </div>
                    <div class='module-dropdown language active'>
                        <div class='input-animation-wrapper'>
                            <label htmlFor={this.languageUUID}>{this.languageSelectLabel}</label>
                            <select
                                id={this.languageUUID}
                                class='custom-select'
                                onChange={event => this.onSelectChange(event as UIEvent)}
                            >
                                {
                                    languages.map(lang => <option
                                        selected={lang.selected}
                                        value={lang.value}
                                    >
                                        {lang.label}
                                    </option>)
                                }
                            </select>
                            <div class='input-animation'></div>
                        </div>
                    </div>
                </div>
                <span class='error-message'></span>
            </div>
        </div>;
    }
}
